import { ModelCollection } from '/@/modules/core/app/models/ModelCollection';
import { User } from '/@/modules/user/models/User';
import { PredefinedProfession } from '/@/modules/professional/enums/PredefinedProfession';
import { minuteDurationToTime } from '/@/modules/core/app/helpers/DateHelper';
import { AuthUser } from '@/modules/auth/models/AuthUser';

export class UserCollection extends ModelCollection<User> {
  constructor(items: User[] | null = null) {
    super(items, User);
  }

  static mapModel(items: User[] | null = null): UserCollection {
    return new this(items);
  }

  get staffProfessionalUsers(): User[] | undefined {
    return this.items?.filter((user: User): boolean => user.isStaff);
  }

  get nonStaffProfessionalUsers(): User[] | undefined {
    return this.items?.filter((user: User): boolean => !user.isStaff);
  }

  get estateProfessionalUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.ESTATE_PROFESSIONAL.id);
  }

  get nonEstateProfessionalUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean =>
        user.professional?.professionId !== PredefinedProfession.ESTATE_PROFESSIONAL.id
    );
  }

  get juniorEstateProfessionalUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.JUNIOR_ESTATE_PROFESSIONAL.id);
  }

  get accountantUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.ACCOUNTANT.id);
  }

  get staffAccountantUsers(): User[] | undefined {
    return this.items?.filter((user: User): boolean => user.isStaffAccountant);
  }

  get staffAllEstateProfessionalUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean =>
        (user.isEstateProfessional || user.isJuniorEstateProfessional) && user.isStaff
    );
  }

  get accountantConsultantUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean =>
        !!user.professional?.isAccountant && !user.isDemo && user.isConsultant
    );
  }

  get lawyerConsultantUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean => !!user.professional?.isLawyer && !user.isDemo && user.isConsultant
    );
  }

  get notaryConsultantUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean => !!user.professional?.isNotary && !user.isDemo && user.isConsultant
    );
  }

  get nonAdvisorAccountantUsers(): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean => !!user.professional?.isAccountant && !user.isDemo && !user.isAdvisor
    );
  }

  get notaryUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.NOTARY.id);
  }

  get financialAdvisorUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.FINANCIAL_ADVISOR.id);
  }

  get trustOfficerUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.TRUST_OFFICER.id);
  }

  get lawyerUsers(): User[] | undefined {
    return this.getUsersWithProfession(PredefinedProfession.LAWYER.id);
  }

  private getUsersWithProfession(professionId: number): User[] | undefined {
    return this.items?.filter(
      (user: User): boolean => user.professional?.professionId === professionId
    );
  }

  get totalTimeEntries(): number | undefined {
    return this.items?.reduce(
      (sum: number, item: User): number => sum + (item.timeEntries?.totalDuration as number),
      0
    );
  }

  get totalTimeEntriesFormatted(): string {
    return minuteDurationToTime(this.totalTimeEntries);
  }

  get namesToString(): string | undefined {
    return this.items?.map((user: User): string => user.name).join(', ') ?? '';
  }

  getEstateAssignedUsers(authUser?: AuthUser): UserCollection | undefined {
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    if (authUser.isSuperAdmin) {
      return new UserCollection(
        this.items?.filter((user: User) => user.professional?.canBeAssignedToEstate(authUser))
      );
    }

    if (!authUser.isStaff) {
      return new UserCollection(
        this.items?.filter(
          (user: User) =>
            user.professional?.canBeAssignedToEstate(authUser) &&
            (user.professional?.isEstateManager ||
              user.professional?.companyId === authUser.professional!.companyId)
        )
      );
    }

    return new UserCollection(
      this.items?.filter(
        (user: User) =>
          user.professional?.canBeAssignedToEstate(authUser) &&
          !user.professional?.company?.isB2BPartner
      )
    );
  }

  getDisabledEstateAssignedUsers(authUser?: AuthUser): UserCollection | undefined {
    // not assignable by auth user
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    if (authUser.isStaffAdminAccountant && authUser.permission?.canAssignUsers) {
      return new UserCollection(
        this.items?.filter((user: User) => !user.professional?.isAccountant)
      );
    }

    if (authUser.isLawyer && authUser.isAdmin) {
      return new UserCollection(this.items?.filter((user: User) => !user.professional?.isLawyer));
    }

    if (authUser.isNotary && authUser.isAdmin) {
      return new UserCollection(this.items?.filter((user: User) => !user.professional?.isNotary));
    }

    if (authUser.permission?.canAssignOnlySelfToEstate) {
      return new UserCollection(this.items?.filter((user: User) => user.id !== authUser.id));
    }

    if (!authUser.permission?.canAssignEstateProfessional) {
      return new UserCollection(
        this.items?.filter((user: User) => user.professional?.isEstateProfessional)
      );
    }

    return new UserCollection([]);
  }

  getAccountantConsultants(authUser?: AuthUser, selfUser?: User): UserCollection | undefined {
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    let users: User[] | undefined = this.accountantConsultantUsers;

    if (selfUser) {
      users = users?.concat(selfUser);
    }

    return new UserCollection(users);
  }

  getNonAdvisorAccountantUsers(authUser?: AuthUser): UserCollection | undefined {
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    return new UserCollection(this.nonAdvisorAccountantUsers);
  }

  getLawyerConsultantUsers(authUser?: AuthUser): UserCollection | undefined {
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    return new UserCollection(this.lawyerConsultantUsers);
  }

  getNotaryConsultantUsers(authUser?: AuthUser): UserCollection | undefined {
    if (!authUser || !this.hasItems) {
      return undefined;
    }

    return new UserCollection(this.notaryConsultantUsers);
  }
}
